import { useEffect, useMemo, useState } from 'react';
import { parseEntityRef } from '@backstage/catalog-model';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';

export type PlatformService = {
  id: string;
  title: string;
  system: string;
  description: string;
  action: string;
  monogram: string;
  color?: string;
  /** Where the tile goes. Absolute URL for an external system. */
  href: string;
  external: boolean;
  /** Where the platform services menu goes; `href` unless overridden. */
  menuHref: string;
  menuExternal: boolean;
  menuAction: string;
  requiredRole?: string;
  /** False when the user lacks `requiredRole`. */
  entitled: boolean;
};

const monogramOf = (title: string) =>
  title
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0])
    .join('')
    .toUpperCase() || '??';

const hasServiceRole = (owned: Set<string>, requiredRole?: string): boolean => {
  if (!requiredRole) return true;
  const role = requiredRole.toLowerCase();
  const prefix = 'group:*/';
  if (!role.startsWith(prefix)) return owned.has(role);

  // A shared service entry has no target tenant yet. The namespace wildcard
  // only unlocks navigation; the target service still authorizes each request.
  const roleName = role.slice(prefix.length);
  return [...owned].some(ref => {
    try {
      const { kind, namespace, name } = parseEntityRef(ref);
      return kind === 'group' && namespace !== '*' && name === roleName;
    } catch {
      return false;
    }
  });
};

/**
 * The services offered by the platform, with the signed-in user's entitlement
 * resolved.
 *
 * A service the user is not entitled to is returned with `entitled: false`
 * rather than dropped. Hiding it would leave the user unable to discover that
 * the platform offers the thing at all, which is the failure the prototype's
 * locked-tile treatment exists to avoid - they see the tile, they see it is
 * locked, and they can ask for access.
 */
export const usePlatformServices = (): {
  loading: boolean;
  services: PlatformService[];
  accessRequestUrl?: string;
} => {
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const [ownership, setOwnership] = useState<string[] | undefined>();

  useEffect(() => {
    let active = true;
    identityApi
      .getBackstageIdentity()
      .then(identity => {
        if (active) setOwnership(identity.ownershipEntityRefs ?? []);
      })
      .catch(() => {
        // An identity lookup that fails must not lock every service tile: the
        // user is signed in, so treat it as "no group memberships known" and
        // let the target system make the real access decision.
        if (active) setOwnership([]);
      });
    return () => {
      active = false;
    };
  }, [identityApi]);

  const configured = useMemo(
    () => configApi.getOptionalConfigArray('aeven.services') ?? [],
    [configApi],
  );

  const accessRequestUrl = useMemo(
    () => configApi.getOptionalString('aeven.accessRequestUrl'),
    [configApi],
  );

  const services = useMemo(() => {
    if (ownership === undefined) return [];
    const owned = new Set(ownership.map(ref => ref.toLowerCase()));

    return configured.map(entry => {
      const title = entry.getString('title');
      const requiredRole = entry.getOptionalString('requiredRole');
      const url = entry.getOptionalString('url');
      const path = entry.getOptionalString('path');
      const menuPath = entry.getOptionalString('menuPath');
      const action = entry.getString('action');
      const href = url ?? path ?? '/';

      return {
        id: entry.getString('id'),
        title,
        system: entry.getString('system'),
        description: entry.getString('description'),
        action,
        monogram: entry.getOptionalString('monogram') ?? monogramOf(title),
        color: entry.getOptionalString('color'),
        href,
        external: Boolean(url),
        menuHref: menuPath ?? href,
        menuExternal: !menuPath && Boolean(url),
        menuAction: entry.getOptionalString('menuAction') ?? action,
        requiredRole,
        entitled: hasServiceRole(owned, requiredRole),
      };
    });
  }, [configured, ownership]);

  return { loading: ownership === undefined, services, accessRequestUrl };
};

/**
 * The URL of a configured service, by id, regardless of whether this user is
 * entitled to it.
 *
 * Exists so a panel that links out to one of these systems - the front page's
 * needs-attention panel links to Grafana - reads the host from the same
 * `aeven.services` block as the tiles instead of hardcoding it. Deliberately
 * independent of `usePlatformServices`: that hook returns an empty list until
 * the identity resolves, and a link that is dead for the first second of every
 * page load is worse than one that is never shown.
 *
 * Returns undefined when the deployment configures no such service, and the
 * caller is expected to drop the link rather than guess a host.
 */
export const useServiceUrl = (id: string): string | undefined => {
  const configApi = useApi(configApiRef);

  return useMemo(
    () =>
      (configApi.getOptionalConfigArray('aeven.services') ?? [])
        .find(entry => entry.getString('id') === id)
        ?.getOptionalString('url'),
    [configApi, id],
  );
};
